import React, { useEffect } from "react";
import { AutoComplete } from "primereact/autocomplete";
import { useToastContext } from "../../../../../../contexts/ToastContext/ToastContext";
import localeText from "../../../../../../locale";
import { useAtecoSearch } from "../../hooks/useAtecoSearch";
import { useUserAtecoCodes } from "../../hooks/useUserAtecoCodes";
import "./UserStoreAtecoSection.css";
import { Button } from "primereact/button";

function buildAssignPayload(item) {
  const description = item.descriptionItalian ?? "";
  return {
    atecoCode: item.atecoCode ?? "",
    customDescription: description,
    effectiveDescription: description,
    id: item.id ?? 0,
    isValidCode: item.isValidCode ?? true,
    primaryActivity: item.primaryActivity ?? true,
  };
}

export default function UserStoreAtecoSection({ hideTitle = false }) {
  const token = localStorage.getItem("__extraconomy_token");
  const { showErrorToast, showSuccessToast } = useToastContext();
  const { query, setQuery, results, loading } = useAtecoSearch();
  const { list, loading: loadingList, assign, remove, refresh } = useUserAtecoCodes();

  useEffect(() => {
    refresh(token);
  }, [refresh, token]);

  const onSelect = async (e) => {
    const item = e.value;
    if (!item || !token) return;
    setQuery("");
    try {
      const payload = buildAssignPayload(item);
      await assign(token, payload);
      const t = localStorage.getItem("__extraconomy_token");
      if (t) refresh(t);
      showSuccessToast({ detail: "atecoAdded" });
    } catch (err) {
      const code = err?.response?.data?.messageCode;
      showErrorToast(code ? { code } : { detail: "atecoAssignError" });
    }
  };

  const onRemove = async (atecoId) => {
    if (!token) return;
    try {
      await remove(token, atecoId);
      showSuccessToast({ detail: "atecoRemoved" });
    } catch (err) {
      const code = err?.response?.data?.messageCode;
      showErrorToast(code ? { code } : { detail: "atecoDeleteError" });
    }
  };

  const itemTemplate = (item) => (
    <div className="flex flex-column gap-1">
      <span className="font-medium">{item.atecoCode}</span>
      {item.descriptionItalian && (
        <span className="text-sm text-500">{item.descriptionItalian}</span>
      )}
    </div>
  );

  return (
    <div className="user-store-ateco-section">
      {!hideTitle && <h2>{localeText("addAteco")}</h2>}
      <AutoComplete
        value={query}
        suggestions={results}
        completeMethod={({ query: q }) => setQuery(q ?? "")}
        onChange={(e) => setQuery(e.value ?? "")}
        onSelect={onSelect}
        itemTemplate={itemTemplate}
        loading={loading}
        placeholder={localeText("atecoSearchPlaceholder")}
        className="w-full"
        inputClassName="w-full border-round-md"
      />
      <div className="flex justify-content-start align-content-center flex-wrap gap-2 mt-2">
        {loadingList ? (
          <span className="text-sm" style={{ color: "var(--theme-text-muted)" }}>
            <i className="pi pi-spin pi-spinner mr-1" />
            {localeText("atecoLoading")}
          </span>
        ) : (
          list.map((item) => (
            <span
              key={item.id}
              className="user-store-ateco-pill border-2 p-2 flex align-items-center gap-2"
              style={{ borderColor: "var(--theme-border)" }}
            >
              <span>{item.atecoCode}</span>
              {item.effectiveDescription && (
                <span
                  className="text-sm overflow-hidden"
                  style={{ color: "var(--theme-text-muted)" }}
                >
                  {item.effectiveDescription}
                </span>
              )}
              <Button
                className="p-button-text p-no-outline border-round-md ateco-pill-remove p-0"
                onClick={() => onRemove(item.id)}
                aria-label={localeText("atecoRemove")}
                icon="pi pi-times"
              />
            </span>
          ))
        )}
      </div>
    </div>
  );
}
